import { useTranslation } from "_metronic/i18n/language";
import Loading from "app/pages/home/components/Loading";
import { AnanseApiService } from "app/services";
import { QuizType } from "app/types";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useParams } from "react-router-dom";
import {
  AccountType,
  QuizAnswerType,
  TesteResultType,
} from "../../../ImportScreen/types";
import LineChartComponent from "./";
import { renderExameHTML } from "./renderExameHTML";
import { renderExameIndetificationHTML } from "./renderExameIndetificationHTML";
import { renderExamePerceptionHTML } from "./renderExamePerception";
import { useSelector } from "react-redux";
import RadarChart from "app/pages/admin/components/RadarChart";
import { imprimirElement } from "app/services/Features";
import { Box, Button } from "app/pages/admin/components";

type TestResult = {
  quizAnswer: TesteResultType;
  account: AccountType;
  quiz: QuizType;
};

type TestResultAnalytic = {
  testResultId: string;
  startDate: string;
  endDate: string;
  totalQuestions: number;
  correctAnswers: number;
  incorrectAnswers: number;
  correctAnswerPercentage: number;
  incorrectAnswersPercentage: number;
};

type TestDataAnalytic = {
  correctAnswerPercentage: number;
  testResults: TestResultAnalytic[];
  totalCorrectAnswers: number;
  totalIncorrectAnswers: number;
  totalQuestions: number;
  totalTests: number;
};

function gerarCodigoNoar() {
  const palavraChave = "NOAR";
  const caracteres = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
  let codigoAleatorio = "";

  for (let i = 0; i < 9; i++) {
    const index = Math.floor(Math.random() * caracteres.length);
    codigoAleatorio += caracteres[index];
  }

  return `${palavraChave}-${codigoAleatorio}`;
}

const codigoVerificacao = gerarCodigoNoar();

const api = new AnanseApiService();
const TestResultsChart: React.FC = () => {
  const params = useParams<{ resultId?: string }>();
  const resultId = params.resultId;
  const translate = useTranslation();

  const [loading, setLoading] = useState<boolean>(true);
  const [timeFormat, setTimeFormat] = useState<"day" | "month">("day");
  const [
    quizAnswerOlfactory,
    setQuizAnswerOlfactory,
  ] = useState<TesteResultType | null>(null);
  const [
    quizAnswerAnalytic,
    setQuizAnswerAnalytic,
  ] = useState<TestDataAnalytic | null>(null);
  const [account, setAccount] = useState<any>(null);
  const [quiz, setQuiz] = useState<any>(null);
  const [chartContainer, setChartContainer] = useState<HTMLElement | null>(
    null,
  );
  const [fragrances, setFragrances] = useState<
    { fragranceId: number; name: string; fragranceNames: any }[]
  >([]);
  const locale = useSelector((state: any) => state.i18n.lang);
  const [questions, setQuestions] = React.useState([]);
  const resultsChartElement = useRef<HTMLDivElement>(null);

  useEffect(() => {
    getFragrances();
  }, []);

  const getFragrances = async () => {
    try {
      const response = await api.makeHttpRequest({ url: "/question" });
      const result = await api.makeHttpRequest({
        url: `/question?PerPage=${response.total}`,
      });
      setQuestions(result.data);
      const { total } = await api.makeHttpRequest({ url: "fragrances" });
      const { data } = await api.makeHttpRequest({
        url: `fragrances?PerPage=${total}`,
      });
      setFragrances(data);
    } catch (error) {
      console.error("Error fetching fragrances:", error);
    }
  };
  const getFragranceName = (fragranceId: number) => {
    const fragrance = fragrances.find(f => f.fragranceId === fragranceId);
    return fragrance;
  };
  const filterQuestion = (value: any) => {
    const quest: any = questions.find((e: any) => {
      return e.questionId === value;
    });

    // If no question is found, return a safe default object
    if (!quest) {
      return {
        questionId: value,
        fragranceId: 0,
        fragrance: {
          fragranceId: 0,
          name: "",
          fragranceNames: {},
        },
      };
    }

    const fragrance = getFragranceName(Number(quest.fragranceId)) || {
      fragranceId: 0,
      name: "",
      fragranceNames: {},
    };
    return { ...quest, fragrance };
  };

  const processData = (answers: QuizAnswerType[], categories: string[]) => {
    const prepareChart: {
      data: number[];
      name: string;
    }[] = [];
    const result = answers.map((answer, i) => {
      const values: Record<string, number> = {};

      // Skip processing otherAnswers if it's null or undefined
      if (answer.otherAnswers) {
        answer.otherAnswers.split(";").forEach(item => {
          const [category, score] = item.split(":").map(str => str.trim());
          const label = pegarPrimeiraPalavra(category);
          if (categories.includes(label)) {
            values[label] = parseInt(score);
          }
        });
      }

      const question = filterQuestion(answer.question);
      const processedItem = {
        questionId: answer.question,
        values: categories.map(category => values[category] || 0),
        shots: answer.shots,
        initDate: answer.initDate,
        endDateTime: answer.endDateTime,
        question,
      };
      const chartData = {
        data: categories.map(category => values[category] || 0),
        name: `${i + 1} - ${question.fragrance?.fragranceNames[
          locale.split("-")[0]
        ] || question.fragrance?.name}`,
      };
      prepareChart.push(chartData);
      return processedItem;
    });
    return { dataTable: result, chartData: prepareChart };
  };

  function pegarPrimeiraPalavra(frase: string): string {
    const trimmedFrase = frase.trim();

    const primeiroEspaco = trimmedFrase.indexOf(" ");

    if (primeiroEspaco === -1) {
      return trimmedFrase;
    } else {
      return trimmedFrase.substring(0, primeiroEspaco);
    }
  }

  const extractCategories = (answers: QuizAnswerType[]): string[] => {
    const categories: Set<string> = new Set();

    answers.forEach(answer => {
      // Skip if otherAnswers is null or undefined
      if (!answer.otherAnswers) {
        return;
      }

      answer.otherAnswers.split(";").forEach(item => {
        const category = item.split(":")[0].trim();
        categories.add(pegarPrimeiraPalavra(category));
      });
    });

    return Array.from(categories);
  };
  const quizAnswers = quizAnswerOlfactory?.quizAnswer || [];

  const categories = extractCategories(quizAnswers);
  const chartData = processData(quizAnswers, categories);

  useEffect(() => {
    if (!resultId) {
      return;
    }
    getQuizAnswerOlfactory(resultId);
  }, [resultId]);

  const getQuizAnswerOlfactory = async (testResultId: string) => {
    setLoading(true);
    const ResponseQuizAnswerOlfactory = await api.makeHttpRequest({
      url: `/quiz/quiz-answers-olfactory/${testResultId}`,
      method: "GET",
    });
    setQuizAnswerOlfactory(ResponseQuizAnswerOlfactory[0]);
    setLoading(false);
  };

  // função para obter o accountId e o QuizId do quizAnswerOlfactory
  const getAccountIdAndQuizId = () => {
    if (!quizAnswerOlfactory || !quizAnswerOlfactory.quizAnswer.length) {
      return { accountId: null, quizId: null };
    }
    const firstQuizAnswer = quizAnswerOlfactory.quizAnswer[0];
    return {
      accountId: firstQuizAnswer.accountId,
      quizId: firstQuizAnswer.quizId,
    };
  };

  // cria um callback para consultar nesse endpoint /api/quiz-analytics/answers-olfactory/{accountId}/{quizId} e o resultado envia para o setQuizAnswerAnalytic
  const getQuizAnswerAnalytic = async () => {
    const { accountId, quizId } = getAccountIdAndQuizId();
    if (!accountId || !quizId) {
      return;
    }
    setLoading(true);
    const response = await api.makeHttpRequest({
      url: `/quiz-analytics/answers-olfactory/${accountId}/${quizId}`,
      method: "GET",
    });
    console.log("Response from /quiz-analytics/answers-olfactory:", response);
    setQuizAnswerAnalytic(response);
    const accountResponse = await api.makeHttpRequest({
      url: `/account/${accountId}`,
      method: "GET",
    });
    setAccount(accountResponse);
    const quizResponse = await api.makeHttpRequest({
      url: `/quiz/${quizId}`,
      method: "GET",
    });
    setQuiz(quizResponse);
    setLoading(false);
  };

  useEffect(() => {
    if (quizAnswerOlfactory) {
      getQuizAnswerAnalytic();
    }
  }, [quizAnswerOlfactory]);

  const getDataResult = useCallback(() => {
    if (!quizAnswerOlfactory || !account || !quiz) {
      return;
    }
    const testData: TestResult = {
      quizAnswer: quizAnswerOlfactory,
      account: account,
      quiz: quiz,
    };
    return testData;
  }, [quizAnswerOlfactory, account, quiz]);

  const testData = getDataResult();

  useEffect(() => {
    if (!testData) {
      return;
    }
    if (
      testData?.quiz.type !== "3" &&
      quizAnswerOlfactory?.quizAnswer[0].otherAnswers
    ) {
      const container = document.getElementById("radar-chart-container");
      setChartContainer(container);
      return;
    }
    if (testData && quizAnswerAnalytic && quizAnswerAnalytic?.totalTests > 1) {
      setTimeout(() => {
        const container = document.getElementById("line-chart-container");
        setChartContainer(container);
      }, 0);
    }
  }, [testData]);

  const dataRendered: any = {
    testResult: testData,
    dataGeracao: new Date().toLocaleString(locale),
    diagnostico: "Diagnóstico de exemplo",
    recomendacoes: [
      {
        texto:
          "Consulta com especialista em otorrinolaringologia para avaliação detalhada",
      },
      {
        texto:
          "Investigação de possíveis causas para diminuição da capacidade olfativa",
      },
      { texto: "Repetição do teste em 3 meses para acompanhamento" },
    ],
    codigoVerificacao: codigoVerificacao,
    mostrarApenasItens: quizAnswerOlfactory?.quizAnswer.length
      ? quizAnswerOlfactory.quizAnswer.length
      : 0,
    children:
      quizAnswerOlfactory?.quizAnswer[0]?.otherAnswers &&
      testData?.quiz?.type !== "3"
        ? `
      <div class="chart-container">
        <h2 class="card-title">${translate("labels_history")}</h2>
        <div id="radar-chart-container"></div>
      </div>
    `
        : quizAnswerAnalytic && quizAnswerAnalytic?.totalTests > 1
        ? `
        <div class="chart-container">
          <h2 class="card-title">${translate("labels_history")}</h2>
          <div id="line-chart-container"></div>
        </div>
      `
        : "",
    translate: translate,
    locale: locale,
    fragrances: fragrances,
    questions: questions,
  };

  return (
    <div className="test-results-container" style={{ padding: "20px" }}>
      <style>
        {`
          .test-results-container {
            position: relative;
          }
          #line-chart-container {
            height: 400px;
            margin: 20px 0;
            width: 100%;
          }
        `}
      </style>

      {loading ? <Loading isLoading /> : null}

      <Box style={{ justifyContent: "end" }}>
        <Button
          onClick={() => {
            imprimirElement(resultsChartElement);
          }}
        >
          {translate("labels_print_pdf")}
        </Button>
      </Box>

      {testData && (
        <div
          ref={resultsChartElement}
          dangerouslySetInnerHTML={{
            __html:
              testData.quiz.type === "3"
                ? renderExameHTML(dataRendered)
                : testData.quizAnswer.quizAnswer[0].otherAnswers
                ? renderExamePerceptionHTML(dataRendered)
                : renderExameIndetificationHTML(dataRendered),
          }}        
        />
      )}

      {/* {chartContainer && quizAnswerAnalytic
        ? createPortal(
            <LineChartComponent
              data={quizAnswerAnalytic}
              timeFormat={timeFormat}
              translate={translate}
            />,
            chartContainer,
          )
        : null} */}
      {chartContainer
        ? quizAnswerOlfactory?.quizAnswer[0]?.otherAnswers &&
          testData?.quiz?.type !== "3"
          ? createPortal(
              <RadarChart labels={categories} series={chartData.chartData} />,
              chartContainer,
            )
          : quizAnswerAnalytic &&
            quizAnswerAnalytic.totalTests > 1 &&
            createPortal(
              <LineChartComponent
                data={quizAnswerAnalytic}
                timeFormat={timeFormat}
                translate={translate}
              />,
              chartContainer,
            )
        : null}
    </div>
  );
};

export default TestResultsChart;
